import { ApiEndpoints } from "../apiEndpoints";
import { requestApi } from "../apiService";
import {
  ClaimAliasRequest,
  ClaimAliasResponse,
  CreateMeetingResponse,
  MintMeetingTokenRequest,
  MintMeetingTokenResponse,
  MyMeeting,
  ResolveAliasResponse,
} from "../types/umbra/meet";

/**
 * Standalone-meeting API client (Product B V0, ow2-chat-be#244).
 *
 * All calls use the shared AUTHED `requestApi` (Bearer + 401-refresh
 * interceptor) — standalone meetings are an authenticated surface in V0
 * (guest join for Product B is a follow-up).
 */

/**
 * Create an instant standalone meeting owned by the caller.
 *
 * The body is `{}` BY CONTRACT: the backend ignores body fields entirely and
 * derives the owner from the authenticated request, so nothing client-supplied
 * can influence ownership.
 */
export async function createMeeting(): Promise<CreateMeetingResponse> {
  const response = await requestApi({
    url: ApiEndpoints.MEET_MEETINGS,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: {},
  });
  return response.data as CreateMeetingResponse;
}

/**
 * Mint a conference token for a standalone meeting — `{ meetingId }` XOR
 * `{ alias }` (the request union makes "both" unrepresentable).
 *
 * The BACKEND decides the role: the meeting owner gets a moderator token, any
 * other authenticated user a member token — there is no owner/member branch on
 * the client. Unknown/expired meetings surface as a generic 404 (`ApiError`
 * with `status === 404` via `requestApi`'s problem+json mapping); callers map
 * that to a not-found state and everything else to a retryable error.
 */
export async function mintMeetingToken(
  request: MintMeetingTokenRequest,
): Promise<MintMeetingTokenResponse> {
  const response = await requestApi({
    url: ApiEndpoints.MEET_MEETINGS_TOKEN,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: request,
  });
  return response.data as MintMeetingTokenResponse;
}

/**
 * Claim a premium vanity alias for the caller's personal meeting.
 *
 * Premium is fail-closed server-side (403 `AliasPremiumRequired`); the caller
 * maps that (and 409 `AliasTaken` / `AliasAlreadyClaimed`, 400 invalid/reserved)
 * from the thrown `ApiError`. Idempotent on re-claiming the SAME alias.
 */
export async function claimAlias(
  request: ClaimAliasRequest,
): Promise<ClaimAliasResponse> {
  const response = await requestApi({
    url: ApiEndpoints.MEET_MEETINGS_CLAIM_ALIAS,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: request,
  });
  return response.data as ClaimAliasResponse;
}

/**
 * Resolve an alias to `{ meetingId, alias, ownerUsername }` — AUTHED, used ONLY
 * for the pre-join "X's room" display. Joining does NOT need this (the token
 * endpoint accepts `{ alias }` directly). Unknown alias → generic 404.
 */
export async function resolveAlias(
  alias: string,
): Promise<ResolveAliasResponse> {
  const response = await requestApi({
    url: ApiEndpoints.MEET_MEETINGS_RESOLVE(alias),
    method: "GET",
  });
  return response.data as ResolveAliasResponse;
}

/** A value is a real meeting only when it carries a truthy string `alias`. */
function asMeetingOrNull(value: unknown): MyMeeting | null {
  if (
    value &&
    typeof value === "object" &&
    typeof (value as { alias?: unknown }).alias === "string" &&
    (value as { alias: string }).alias.length > 0
  ) {
    return value as MyMeeting;
  }
  return null;
}

/**
 * Fetch the caller's personal (vanity) meeting, or `null` when none exists.
 *
 * The documented contract returns the meeting's fields at the TOP LEVEL
 * (`{ meetingId, alias, room, … }`) and signals "no meeting" as `{ meeting: null }`,
 * `{}`, or a bare null. To stay robust against the sibling BE choosing the
 * wrapped envelope instead (`{ meeting: {…} }`), this also unwraps a nested
 * `meeting` object. Everything without a truthy `alias` normalizes to `null` so
 * the UI has ONE shape.
 */
export async function getMyMeeting(): Promise<MyMeeting | null> {
  const response = await requestApi({
    url: ApiEndpoints.MEET_MEETINGS_MINE,
    method: "GET",
  });
  const data = response.data as unknown;
  const nested =
    data && typeof data === "object"
      ? (data as { meeting?: unknown }).meeting
      : undefined;
  return asMeetingOrNull(data) ?? asMeetingOrNull(nested);
}
